import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, BookOpen, Heart, Users, Plus, FileText, Video, CheckCircle2, Circle, Rocket, X, Bold, Italic, Link2, Image as ImageIcon, Heading2, List } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FileUpload } from "../components/FileUpload";

const MD_COMPONENTS = {
  img: ({ node, ...props }) => <img {...props} alt={props.alt || ""} className="w-full my-4 rounded" />,
  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-pti-red underline" />,
};

const CATEGORIES = ["Tech & AI Trends", "Creative Business", "Design & Visual Arts", "Innovation & Startups", "Lifestyle & Culture", "Inspiration & People", "Gaming", "Digital Culture"];

const STATUS_LABEL = {
  draft: ["Draft", "bg-gray-100 text-gray-700"],
  under_review: ["Under Review", "bg-yellow-100 text-yellow-800"],
  approved: ["Approved", "bg-green-100 text-green-800"],
  needs_revision: ["Needs Revision", "bg-orange-100 text-orange-800"],
  rejected: ["Rejected", "bg-red-100 text-red-700"],
  removed: ["Removed", "bg-red-100 text-red-700"],
};

function slugify(s) { return s.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 80); }

export default function CreatorDashboard() {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({ articles: [], shorts: [], totals: {} });
  const load = () => api.get("/creator/content").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const T = data.totals;
  return (
    <div className="pti-container py-10" data-testid="creator-dashboard">
      <h1 className="font-display text-4xl font-black">Creator Dashboard</h1>
      <div className="flex gap-5 mt-5 border-b border-gray-200 overflow-x-auto" role="tablist">
        {["overview", "write", "upload", "content"].map((t) => (
          <button key={t} onClick={() => setTab(t)} role="tab" aria-selected={tab === t} data-testid={`cd-tab-${t}`}
            className={`text-sm font-bold uppercase tracking-wider pb-2 border-b-2 whitespace-nowrap transition-colors ${tab === t ? "border-pti-red text-pti-black" : "border-transparent text-gray-500"}`}>{t}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-8">
          <WelcomeChecklist data={data} goTo={setTab} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["Views", T.views, Eye], ["Reads", T.reads, BookOpen], ["Likes", T.likes, Heart], ["Followers", T.followers, Users]].map(([l, v, I]) => (
              <div key={l} className="border border-gray-200 p-5">
                <I size={18} className="text-pti-red" />
                <div className="font-display text-3xl font-bold mt-2">{v || 0}</div>
                <div className="text-xs uppercase tracking-wider text-gray-500">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => setTab("write")} className="flex items-center gap-2 bg-pti-red text-white px-5 py-2.5 font-bold hover:bg-pti-redhover transition-colors"><Plus size={16} /> Write article</button>
            <button onClick={() => setTab("upload")} className="flex items-center gap-2 border border-pti-black px-5 py-2.5 font-bold hover:bg-pti-black hover:text-white transition-colors"><Video size={16} /> Upload Short</button>
          </div>
        </div>
      )}

      {tab === "write" && <ArticleEditor onSaved={() => { load(); setTab("content"); }} />}
      {tab === "upload" && <ShortUploader onSaved={() => { load(); setTab("content"); }} />}
      {tab === "content" && <ContentList data={data} reload={load} />}
    </div>
  );
}

function WelcomeChecklist({ data, goTo }) {
  const { user } = useAuth();
  const key = `pti_checklist_dismissed_${user?.id || "anon"}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(key) === "1");

  const articles = data.articles || [];
  const shorts = data.shorts || [];
  const hasDraft = articles.length > 0 || shorts.length > 0;
  const hasSubmitted = [...articles, ...shorts].some((c) => c.status && c.status !== "draft");
  const hasPublished = [...articles, ...shorts].some((c) => c.status === "approved");

  const steps = [
    { done: hasDraft, title: "Write your first story", desc: "Draft an article or upload a Short.", cta: "Start writing", action: () => goTo("write") },
    { done: hasSubmitted, title: "Submit it for review", desc: "Send your draft to our editors from the Content tab.", cta: "Go to Content", action: () => goTo("content") },
    { done: hasPublished, title: "Get published on PictureThisInk", desc: "Once approved, your story goes live to readers.", cta: "See status", action: () => goTo("content") },
  ];
  const completed = steps.filter((s) => s.done).length;
  const allDone = completed === steps.length;

  if (dismissed || allDone) return null;

  const dismiss = () => { localStorage.setItem(key, "1"); setDismissed(true); };

  return (
    <div className="border-2 border-pti-black mb-8" data-testid="welcome-checklist">
      <div className="flex items-center justify-between gap-3 px-5 py-4 bg-pti-black text-white">
        <div className="flex items-center gap-2">
          <Rocket size={18} className="text-pti-red" />
          <h2 className="font-display text-lg font-bold">Get started — publish your first story</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-neutral-300">{completed}/{steps.length} done</span>
          <button onClick={dismiss} data-testid="checklist-dismiss" aria-label="Dismiss checklist" className="text-neutral-400 hover:text-white"><X size={16} /></button>
        </div>
      </div>
      <div className="h-1 bg-gray-200"><div className="h-1 bg-pti-red transition-all" style={{ width: `${(completed / steps.length) * 100}%` }} /></div>
      <ol className="divide-y divide-gray-100">
        {steps.map((s, i) => (
          <li key={i} className="flex items-center justify-between gap-3 px-5 py-4" data-testid={`checklist-step-${i}`}>
            <div className="flex items-start gap-3 min-w-0">
              {s.done ? <CheckCircle2 size={20} className="text-green-600 shrink-0 mt-0.5" /> : <Circle size={20} className="text-gray-300 shrink-0 mt-0.5" />}
              <div className="min-w-0">
                <div className={`font-bold ${s.done ? "line-through text-gray-400" : ""}`}>{s.title}</div>
                {!s.done && <div className="text-sm text-gray-500">{s.desc}</div>}
              </div>
            </div>
            {!s.done && <button onClick={s.action} data-testid={`checklist-cta-${i}`} className="shrink-0 text-xs font-bold px-3 py-1.5 border border-pti-red text-pti-red hover:bg-pti-red hover:text-white transition-colors">{s.cta}</button>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function ArticleEditor({ onSaved }) {
  const [f, setF] = useState({ title: "", category: CATEGORIES[0], tags: "", cover_image: "", cover_alt: "", excerpt: "", body: "", slug: "" });
  const [preview, setPreview] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const bodyRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, ...(k === "title" ? { slug: slugify(e.target.value) } : {}) });

  const applyToBody = (transform) => {
    const ta = bodyRef.current;
    const val = f.body;
    const s = ta ? ta.selectionStart : val.length;
    const e = ta ? ta.selectionEnd : val.length;
    const { text, cs, ce } = transform(val.slice(s, e), val, s, e);
    setF((p) => ({ ...p, body: text }));
    requestAnimationFrame(() => { if (ta) { ta.focus(); ta.setSelectionRange(cs, ce); } });
  };
  const wrap = (before, after, placeholder) => applyToBody((sel, val, s, e) => {
    const inner = sel || placeholder;
    const text = val.slice(0, s) + before + inner + after + val.slice(e);
    return { text, cs: s + before.length, ce: s + before.length + inner.length };
  });
  const insertBlock = (block) => applyToBody((sel, val, s, e) => {
    const pre = val.slice(0, e), post = val.slice(e);
    const lead = pre && !pre.endsWith("\n\n") ? "\n\n" : "";
    const tail = post && !post.startsWith("\n\n") ? "\n\n" : "";
    const text = pre + lead + block + tail + post;
    const pos = (pre + lead + block).length;
    return { text, cs: pos, ce: pos };
  });
  const addLink = () => { const url = window.prompt("Link URL (https://…)"); if (url) wrap("[", `](${url})`, "link text"); };
  const insertImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImgBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      insertBlock(`![image description](${process.env.REACT_APP_BACKEND_URL}${data.url})`);
      toast.success(data.compressed ? "Image added (optimized to fit)." : "Image added to body.");
    } catch (err) { toast.error(`Upload failed: ${apiErr(err)}`); }
    setImgBusy(false); e.target.value = "";
  };

  const save = async (submit) => {
    if (!f.title || !f.body) return toast.error("Title and body are required.");
    if (submit && !f.cover_alt) return toast.error("Alt text for the cover image is required before submitting.");
    try {
      const payload = { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      const { data } = await api.post("/creator/articles", payload);
      if (submit) { await api.post(`/creator/articles/${data.id}/submit`); toast.success("Submitted for review!"); }
      else toast.success("Draft saved.");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const TB = ({ onClick, children, testid, title }) => (
    <button type="button" onClick={onClick} data-testid={testid} title={title} aria-label={title}
      className="flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-white hover:text-pti-red border border-transparent hover:border-gray-300 transition-colors">{children}</button>
  );

  return (
    <div className="mt-8 max-w-3xl" data-testid="article-editor">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">New Article</h2>
        <button onClick={() => setPreview(!preview)} data-testid="editor-preview" className="text-sm font-bold text-pti-red">{preview ? "Edit" : "Preview"}</button>
      </div>
      {preview ? (
        <div className="mt-4 border border-gray-200 p-6">
          <span className="text-[11px] uppercase font-bold text-pti-red">{f.category}</span>
          <h1 className="font-display text-3xl font-black mt-1">{f.title || "Untitled"}</h1>
          {f.cover_image && <img src={f.cover_image} alt={f.cover_alt} className="w-full aspect-video object-cover mt-4" />}
          <div className="prose max-w-none mt-4 text-gray-800"><ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{f.body || "_Nothing to preview yet._"}</ReactMarkdown></div>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <In label="Title" v={f.title} on={set("title")} testid="art-title" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm font-bold">Category</label>
              <select value={f.category} onChange={set("category")} data-testid="art-category" className="w-full border border-gray-300 px-3 py-2.5 mt-1 outline-none focus:border-pti-red">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <In label="SEO slug" v={f.slug} on={set("slug")} testid="art-slug" />
          </div>
          <In label="Tags (comma separated)" v={f.tags} on={set("tags")} testid="art-tags" />
          <FileUpload label="Upload cover image" accept="image/*" testid="art-cover-upload" onUploaded={(url) => setF((p) => ({ ...p, cover_image: url }))} hint="JPG, PNG, WebP or GIF. Large images are optimized automatically. Or paste a URL below." />
          {f.cover_image && <img src={f.cover_image} alt="Cover preview" className="w-full max-h-48 object-contain border border-gray-200" />}
          <In label="Cover image URL" v={f.cover_image} on={set("cover_image")} testid="art-cover" />
          <In label="Cover image alt text (required for accessibility)" v={f.cover_alt} on={set("cover_alt")} testid="art-alt" />
          <In label="Excerpt" v={f.excerpt} on={set("excerpt")} testid="art-excerpt" />
          <div>
            <label className="text-sm font-bold">Body</label>
            <div className="flex flex-wrap items-center gap-0.5 border border-gray-300 border-b-0 p-1 bg-gray-50 mt-1" data-testid="art-body-toolbar" role="toolbar" aria-label="Formatting">
              <TB onClick={() => insertBlock("## Heading")} testid="tb-h2" title="Heading"><Heading2 size={16} /></TB>
              <TB onClick={() => wrap("**", "**", "bold text")} testid="tb-bold" title="Bold"><Bold size={16} /></TB>
              <TB onClick={() => wrap("*", "*", "italic text")} testid="tb-italic" title="Italic"><Italic size={16} /></TB>
              <TB onClick={() => insertBlock("- List item")} testid="tb-list" title="List"><List size={16} /></TB>
              <TB onClick={addLink} testid="tb-link" title="Insert link"><Link2 size={16} /></TB>
              <label data-testid="tb-image" title="Insert image" aria-label="Insert image"
                className="flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-white hover:text-pti-red border border-transparent hover:border-gray-300 cursor-pointer transition-colors">
                {imgBusy ? <span className="text-[10px] font-bold">…</span> : <ImageIcon size={16} />}
                <input type="file" accept="image/*" onChange={insertImage} className="hidden" />
              </label>
              <span className="ml-auto pr-1 text-[10px] text-gray-400">Markdown supported</span>
            </div>
            <textarea ref={bodyRef} value={f.body} onChange={set("body")} rows={12} data-testid="art-body" placeholder="Write your story… Use the toolbar to add headings, bold, links, and images. Separate paragraphs with a blank line."
              className="w-full border border-gray-300 p-3 outline-none focus:border-pti-red" />
          </div>
        </div>
      )}
      <div className="flex gap-3 mt-5">
        <button onClick={() => save(false)} data-testid="art-save-draft" className="border border-pti-black px-5 py-2.5 font-bold hover:bg-gray-100 transition-colors">Save draft</button>
        <button onClick={() => save(true)} data-testid="art-submit" className="bg-pti-red text-white px-5 py-2.5 font-bold hover:bg-pti-redhover transition-colors">Submit for review</button>
      </div>
    </div>
  );
}

function ShortUploader({ onSaved }) {
  const [f, setF] = useState({ title: "", caption: "", category: CATEGORIES[0], tags: "", video_url: "", thumbnail: "", ownership_confirmed: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.caption.trim()) return toast.error("A caption is required for every Short.");
    if (!f.ownership_confirmed) return toast.error("Please confirm you own the rights to this content.");
    try {
      await api.post("/creator/shorts", { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Short submitted for review!");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mt-8 max-w-2xl space-y-4" data-testid="short-uploader">
      <h2 className="font-display text-2xl font-bold">Upload a Short</h2>
      <In label="Title" v={f.title} on={set("title")} testid="short-title" />
      <div><label className="text-sm font-bold">Caption (required)</label>
        <textarea value={f.caption} onChange={set("caption")} rows={3} data-testid="short-caption" className="w-full border border-gray-300 p-3 mt-1 outline-none focus:border-pti-red" />
      </div>
      <div><label className="text-sm font-bold">Category</label>
        <select value={f.category} onChange={set("category")} data-testid="short-category" className="w-full border border-gray-300 px-3 py-2.5 mt-1 outline-none focus:border-pti-red">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
      </div>
      <In label="Tags (comma separated)" v={f.tags} on={set("tags")} testid="short-tags" />
      <FileUpload label="Upload video" accept="video/*" testid="short-video-upload" onUploaded={(url) => setF((p) => ({ ...p, video_url: url }))} hint="MP4, WebM or MOV (max 200MB). Or paste a URL below." />
      {f.video_url && <video src={f.video_url} controls className="w-full max-h-56 bg-black" />}
      <In label="Video URL (MP4)" v={f.video_url} on={set("video_url")} testid="short-video" />
      <FileUpload label="Upload thumbnail" accept="image/*" testid="short-thumb-upload" onUploaded={(url) => setF((p) => ({ ...p, thumbnail: url }))} hint="Or paste a URL below." />
      <In label="Thumbnail URL" v={f.thumbnail} on={set("thumbnail")} testid="short-thumb" />
      <label className="flex items-center gap-2 text-sm" data-testid="short-ownership">
        <input type="checkbox" checked={f.ownership_confirmed} onChange={(e) => setF({ ...f, ownership_confirmed: e.target.checked })} className="w-4 h-4" />
        I confirm I own the rights to this content and it follows the Community Guidelines.
      </label>
      <button onClick={submit} data-testid="short-submit" className="bg-pti-red text-white px-5 py-2.5 font-bold hover:bg-pti-redhover transition-colors">Submit for review</button>
    </div>
  );
}

function ContentList({ data, reload }) {
  const submit = async (id) => { try { await api.post(`/creator/articles/${id}/submit`); toast.success("Submitted!"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="mt-8 space-y-8" data-testid="content-list">
      <section>
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><FileText size={20} /> Articles</h2>
        <div className="mt-3 divide-y divide-gray-200 border border-gray-200">
          {data.articles.length === 0 && <p className="p-4 text-gray-400 text-sm">No articles yet.</p>}
          {data.articles.map((a) => {
            const [label, cls] = STATUS_LABEL[a.status] || [a.status, "bg-gray-100"];
            return (
              <div key={a.id} className="p-4 flex items-center justify-between gap-3">
                <div><div className="font-semibold">{a.title}</div><div className="text-xs text-gray-500">{a.category} · {a.views} views · {a.likes} likes</div></div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[11px] font-bold px-2 py-1 ${cls}`}>{label}</span>
                  {a.status === "approved" && <Link to={`/article/${a.slug}`} className="text-xs font-bold text-pti-red">View</Link>}
                  {(a.status === "draft" || a.status === "needs_revision") && <button onClick={() => submit(a.id)} data-testid={`submit-${a.id}`} className="text-xs font-bold text-pti-red">Submit</button>}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section>
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Video size={20} /> Shorts</h2>
        <div className="mt-3 divide-y divide-gray-200 border border-gray-200">
          {data.shorts.length === 0 && <p className="p-4 text-gray-400 text-sm">No Shorts yet.</p>}
          {data.shorts.map((s) => {
            const [label, cls] = STATUS_LABEL[s.status] || [s.status, "bg-gray-100"];
            return <div key={s.id} className="p-4 flex items-center justify-between"><div className="font-semibold">{s.title}</div><span className={`text-[11px] font-bold px-2 py-1 ${cls}`}>{label}</span></div>;
          })}
        </div>
      </section>
    </div>
  );
}

function In({ label, v, on, testid }) {
  return <div><label className="text-sm font-bold">{label}</label><input value={v} onChange={on} data-testid={testid} className="w-full border border-gray-300 px-3 py-2.5 mt-1 outline-none focus:border-pti-red" /></div>;
}
